/**
 * Recipes — the native mod format.
 *
 * A recipe is a JSON document of *field-level changes to game objects*. It
 * contains no game bytes at all: "pid 11, maxDamage 50, name Big Iron" is the
 * whole mod. `compile()` reads the user's own files through a `getFile`
 * callback, applies the named changes, and returns an overlay of loose files the
 * engine mounts through mods_order.txt. Nothing Interplay ever shipped is in the
 * document, which is what makes a recipe the cleanest object we can host.
 *
 * The property that pays for the format is field-level merge. Two recipes that
 * touch the same item but different fields compose exactly; a whole-file overlay
 * cannot do this, because the loader resolves conflicts by whole-file
 * replacement (angle 4, xfile.cc). Same-field collisions are surfaced to the
 * caller as data — never resolved silently — and load order decides.
 *
 * The full specification lives in docs/design/recipe-format.md.
 */

import {
  ITEM_HEADER_FIELDS,
  ITEM_SUBTYPE_FIELDS,
  ITEM_TYPES,
  type FieldSpec,
} from './fields';
import {
  cloneItemProto,
  conventionalMessageId,
  diffItemProto,
  parseItemProto,
  protoNumberFromPid,
  writeItemProto,
  type ItemProtoFile,
} from './proto-codec';
import {
  applyMsgEdits,
  assertMsgSafe,
  diffMsg,
  getEntry,
  parseMsg,
  type MsgEdit,
  type MsgFile,
} from './msg-codec';
import { LST_FILLER, parseLst, serializeLst, lookupProtoFile, setLine, claimedNumbers } from './lst-codec';

export class RecipeError extends Error {}

export const RECIPE_SCHEMA = 1;

/** Proto numbers a recipe may claim for new items. Below 4000 is Interplay's. */
export const NEW_ITEM_BAND: [number, number] = [4000, 9999];

/** Message offsets from a proto's own messageId (engine/src/proto.h:96-97). */
export const MESSAGE_OFFSET_NAME = 0;
export const MESSAGE_OFFSET_DESCRIPTION = 1;

/** Path of the item proto message file, relative to `text/<language>/`. */
export const ITEM_MSG_FILE = 'game/pro_item.msg';

export interface ItemTarget {
  fields?: Record<string, number>;
  name?: string;
  description?: string;
}

export interface NewItem {
  /** Absolute proto number in the reserved band. Also the pid, since items are type 0. */
  pid: number;
  /** pid of an existing item this one is cloned from. Supplies art, script and item type. */
  template: number;
  name: string;
  description?: string;
  fields?: Record<string, number>;
}

export interface Recipe {
  schema: number;
  id: string;
  name: string;
  version: string;
  author?: string;
  /** Minimum engine build this recipe expects. Advisory; the installer warns. */
  engineMin?: string;
  /** Free-text note shown on the mod page. */
  notes?: string;
  /**
   * Keys are `item:<pid>` (an ItemTarget) or `msg:<file>:<num>` (a string), where
   * `<file>` is relative to `text/<language>/`.
   */
  targets?: Record<string, ItemTarget | string>;
  newItems?: NewItem[];
}

export interface CompileOptions {
  /** Which `text/<language>/` tree to write. Defaults to english. */
  language?: string;
  /** Root the emitted paths are relative to. Defaults to '' (the game root). */
  prefix?: string;
}

export type GetFile = (path: string) => Promise<Uint8Array | null> | Uint8Array | null;

export interface OverlayFile {
  path: string;
  bytes: Uint8Array;
}

export interface CompileResult {
  files: OverlayFile[];
  warnings: string[];
  /** Every pid this recipe reads or writes — the installer's savesRequired input. */
  touchedPids: number[];
}

// ---------------------------------------------------------------- validation

const FIELD_INDEX: Map<string, FieldSpec[]> = (() => {
  const idx = new Map<string, FieldSpec[]>();
  const add = (s: FieldSpec) => idx.set(s.name, [...(idx.get(s.name) ?? []), s]);
  ITEM_HEADER_FIELDS.forEach(add);
  for (const type of ITEM_TYPES) ITEM_SUBTYPE_FIELDS[type].forEach(add);
  return idx;
})();

/** Every field name a recipe may set, with its documentation. For the editor UI. */
export function editableFields(): { name: string; doc: string; range?: [number, number] }[] {
  const out: { name: string; doc: string; range?: [number, number] }[] = [];
  for (const [name, specs] of FIELD_INDEX) {
    const s = specs[0];
    if (!s.edit) continue;
    out.push(s.range ? { name, doc: s.doc, range: s.range } : { name, doc: s.doc });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export interface ValidationIssue {
  /** Dotted path into the recipe document, so an editor can focus the field. */
  at: string;
  message: string;
}

const ID_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;
const VERSION_RE = /^\d+\.\d+\.\d+(?:-[0-9a-z.-]+)?$/i;
const ITEM_KEY_RE = /^item:(-?\d+)$/;
const MSG_KEY_RE = /^msg:([a-z0-9_./-]+\.msg):(-?\d+)$/i;

/**
 * Schema and range check. Deliberately base-independent: a recipe declares
 * nothing about the bytes it will be applied to, so validation cannot know an
 * item's subtype. Per-subtype legality (does a `key` item have `maxDamage`?) is
 * enforced by `compile()`, where the base proto is in hand.
 */
export function validate(recipe: unknown): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const bad = (at: string, message: string) => issues.push({ at, message });

  if (typeof recipe !== 'object' || recipe === null) {
    return [{ at: '', message: 'A recipe must be a JSON object.' }];
  }
  const r = recipe as Partial<Recipe>;

  if (r.schema !== RECIPE_SCHEMA) {
    bad('schema', `Unsupported schema ${String(r.schema)}; this build reads schema ${RECIPE_SCHEMA}.`);
  }
  if (typeof r.id !== 'string' || !ID_RE.test(r.id)) {
    bad('id', 'id must be 2-64 characters of lowercase letters, digits and hyphens — it becomes the mods/<id>/ directory.');
  }
  if (typeof r.name !== 'string' || r.name.trim().length === 0) bad('name', 'name is required.');
  if (typeof r.version !== 'string' || !VERSION_RE.test(r.version)) {
    bad('version', 'version must look like 1.0.0 — installs compare versions when a save declares this mod.');
  }
  if (r.author !== undefined && typeof r.author !== 'string') bad('author', 'author must be a string.');

  const targets = r.targets ?? {};
  if (typeof targets !== 'object' || targets === null) {
    bad('targets', 'targets must be an object keyed by item:<pid> or msg:<file>:<num>.');
  } else {
    for (const [key, value] of Object.entries(targets)) {
      const itemMatch = ITEM_KEY_RE.exec(key);
      const msgMatch = MSG_KEY_RE.exec(key);
      if (itemMatch) {
        validateItemTarget(`targets.${key}`, Number(itemMatch[1]), value, bad);
      } else if (msgMatch) {
        if (typeof value !== 'string') {
          bad(`targets.${key}`, 'A msg: target must be the replacement string.');
        } else {
          try {
            assertMsgSafe(value);
          } catch (e) {
            bad(`targets.${key}`, (e as Error).message);
          }
        }
      } else {
        bad(`targets.${key}`, 'Unrecognised target. Use item:<pid> or msg:<file>:<num> (file relative to text/<language>/).');
      }
    }
  }

  const newItems = r.newItems ?? [];
  if (!Array.isArray(newItems)) {
    bad('newItems', 'newItems must be an array.');
  } else {
    const seen = new Set<number>();
    newItems.forEach((item, i) => {
      const at = `newItems[${i}]`;
      if (typeof item !== 'object' || item === null) return bad(at, 'Each new item must be an object.');
      const n = item as Partial<NewItem>;
      if (!Number.isInteger(n.pid) || (n.pid as number) < NEW_ITEM_BAND[0] || (n.pid as number) > NEW_ITEM_BAND[1]) {
        bad(`${at}.pid`, `New items must claim a pid in the reserved band ${NEW_ITEM_BAND[0]}-${NEW_ITEM_BAND[1]}; below that is Interplay's numbering.`);
      } else if (seen.has(n.pid as number)) {
        bad(`${at}.pid`, `pid ${n.pid} is claimed twice by this recipe.`);
      } else {
        seen.add(n.pid as number);
      }
      if (!Number.isInteger(n.template)) bad(`${at}.template`, 'template must be the pid of an existing item to clone.');
      if (typeof n.name !== 'string' || !n.name.trim()) bad(`${at}.name`, 'A new item needs a name.');
      else {
        try {
          assertMsgSafe(n.name);
        } catch (e) {
          bad(`${at}.name`, (e as Error).message);
        }
      }
      if (n.description !== undefined) {
        try {
          assertMsgSafe(n.description);
        } catch (e) {
          bad(`${at}.description`, (e as Error).message);
        }
      }
      if (n.fields !== undefined) validateFields(`${at}.fields`, n.fields, bad);
    });
  }

  return issues;
}

function validateItemTarget(at: string, pid: number, value: unknown, bad: (at: string, m: string) => void): void {
  if ((pid >> 24) & 0xff) {
    bad(at, `pid ${pid} is not an item (its high byte names another object type). v1 edits items only.`);
  }
  if (typeof value !== 'object' || value === null) {
    return bad(at, 'An item: target must be an object with fields, name and/or description.');
  }
  const t = value as ItemTarget;
  for (const key of Object.keys(t)) {
    if (!['fields', 'name', 'description'].includes(key)) {
      bad(`${at}.${key}`, `Unknown key "${key}" on an item target. Only fields, name and description exist.`);
    }
  }
  for (const key of ['name', 'description'] as const) {
    const v = t[key];
    if (v === undefined) continue;
    if (typeof v !== 'string') {
      bad(`${at}.${key}`, `${key} must be a string.`);
      continue;
    }
    try {
      assertMsgSafe(v);
    } catch (e) {
      bad(`${at}.${key}`, (e as Error).message);
    }
  }
  if (t.fields !== undefined) validateFields(`${at}.fields`, t.fields, bad);
}

function validateFields(at: string, fields: unknown, bad: (at: string, m: string) => void): void {
  if (typeof fields !== 'object' || fields === null) return bad(at, 'fields must be an object of fieldName: number.');
  for (const [name, value] of Object.entries(fields as Record<string, unknown>)) {
    const specs = FIELD_INDEX.get(name);
    if (!specs) {
      bad(`${at}.${name}`, `There is no item field called "${name}".`);
      continue;
    }
    const spec = specs[0];
    if (!spec.edit) {
      bad(`${at}.${name}`, `"${name}" cannot be set by a recipe: ${spec.why ?? 'read-only'}.`);
      continue;
    }
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      bad(`${at}.${name}`, `"${name}" must be a whole number.`);
      continue;
    }
    // Where the same name exists on several subtypes, accept the widest range;
    // compile() narrows it once the real subtype is known.
    const lo = Math.min(...specs.map((s) => s.range?.[0] ?? -2147483648));
    const hi = Math.max(...specs.map((s) => s.range?.[1] ?? 2147483647));
    if (value < lo || value > hi) {
      bad(`${at}.${name}`, `"${name}" must be between ${lo} and ${hi} (got ${value}).`);
    }
  }
}

export function assertValid(recipe: unknown): asserts recipe is Recipe {
  const issues = validate(recipe);
  if (issues.length) {
    throw new RecipeError(
      `This recipe is not valid:\n${issues.map((i) => `  ${i.at || '(root)'}: ${i.message}`).join('\n')}`
    );
  }
}

// ------------------------------------------------------------------- compile

interface ProtoDir {
  lst: ReturnType<typeof parseLst>;
  /** proto number -> loaded base proto. */
  loaded: Map<number, ItemProtoFile>;
}

async function readFile(getFile: GetFile, path: string): Promise<Uint8Array | null> {
  return await getFile(path);
}

async function requireFile(getFile: GetFile, path: string, why: string): Promise<Uint8Array> {
  const bytes = await readFile(getFile, path);
  if (!bytes) throw new RecipeError(`Cannot compile: ${path} is missing from your game files (needed to ${why}).`);
  return bytes;
}

async function loadProto(dir: ProtoDir, getFile: GetFile, pid: number): Promise<ItemProtoFile> {
  const n = protoNumberFromPid(pid);
  const cached = dir.loaded.get(n);
  if (cached) return cached;
  const file = lookupProtoFile(dir.lst, n);
  if (!file || file === LST_FILLER) {
    throw new RecipeError(`No item with pid ${pid}: items.lst has no entry at line ${n}.`);
  }
  const bytes = await requireFile(getFile, `proto/items/${file.toLowerCase()}`, `read item ${pid}`);
  const proto = parseItemProto(bytes);
  dir.loaded.set(n, proto);
  return proto;
}

// ----------------------------------------------------------------- art checks

/** `gArtListDescriptions` (art.cc:79), indexed by a fid's object-type nibble. */
const ART_LIST_NAMES = [
  'items',
  'critters',
  'scenery',
  'walls',
  'tiles',
  'misc',
  'intrface',
  'inven',
  'heads',
  'backgrnd',
  'skilldex',
] as const;

/**
 * How many entries an `art/<dir>/<dir>.lst` declares. The art lists are
 * **0-based** (`artBuildFilePath` indexes `fileNames + frmId * 13` directly,
 * art.cc:668) unlike proto/items/items.lst, and `artReadList` (art.cc:722) cuts
 * each line at the first of ` ,;\r\t\n`, so a trailing comment is not part of a
 * name. A line that cuts to nothing is not an entry the engine can open.
 */
function artListLength(bytes: Uint8Array): number {
  let text = '';
  for (let i = 0; i < bytes.length; i++) text += String.fromCharCode(bytes[i]);
  const lines = text.split(/\r?\n/);
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  return lines.length;
}

/**
 * Per-install art checks. These are **warnings, never errors**: a recipe
 * declares nothing about the bytes it will be applied to (see
 * docs/design/recipe-format.md §Versioning), so an index that names nothing here
 * may name the right art on the install the author cut it against. The engine
 * agrees about the stakes — `artBuildFilePath` bounds-checks frmId against the
 * list length (art.cc:689) and an invalid fid draws silent nothing rather than
 * crashing (art.cc:427-430). We check the index only; the .frm behind it is not
 * opened, because a list line is the contract the fid actually names.
 */
class ArtChecker {
  private readonly getFile: GetFile;
  private readonly counts = new Map<string, number | null>();
  readonly warnings: string[] = [];

  constructor(getFile: GetFile) {
    this.getFile = getFile;
  }

  /** Entry count of one art list, read at most once per compile. */
  private async count(listName: string): Promise<number | null> {
    const cached = this.counts.get(listName);
    if (cached !== undefined) return cached;
    const bytes = await readFile(this.getFile, `art/${listName}/${listName}.lst`);
    const n = bytes ? artListLength(bytes) : null;
    this.counts.set(listName, n);
    if (n === null) {
      this.warnings.push(
        `art/${listName}/${listName}.lst is missing from your game files, so art indexes into it could not be checked.`
      );
    }
    return n;
  }

  async check(label: string, fields: Record<string, number> | undefined): Promise<void> {
    if (!fields) return;
    for (const name of ['fid', 'inventoryFid'] as const) {
      const fid = fields[name];
      if (fid === undefined || fid < 0) continue;
      const listName = ART_LIST_NAMES[(fid >>> 24) & 0x0f];
      if (!listName) {
        this.warnings.push(
          `${label}: ${name} 0x${(fid >>> 0).toString(16)} has object type ${(fid >>> 24) & 0x0f}, which names no art list — the game will draw nothing for it.`
        );
        continue;
      }
      const n = await this.count(listName);
      if (n === null) continue;
      const frmId = fid & 0xfff;
      if (frmId >= n) {
        this.warnings.push(
          `${label}: ${name} points at line ${frmId} of art/${listName}/${listName}.lst, which has ${n} lines on this install. ` +
            'The item will be invisible rather than crash; check the index against the copy of the game you authored against.'
        );
      }
    }
    for (const name of ['maleFid', 'femaleFid'] as const) {
      const index = fields[name];
      if (index === undefined || index < 0) continue;
      const n = await this.count('critters');
      if (n === null) continue;
      if (index >= n) {
        this.warnings.push(
          `${label}: ${name} is line ${index} of art/critters/critters.lst, which has ${n} lines on this install. ` +
            'Wearing the armor would replace the wearer with art that does not exist here.'
        );
      }
    }
  }
}

/**
 * Turns a recipe into the loose-file overlay the engine mounts. Reads only
 * through `getFile`; writes nothing. The caller decides where the files land
 * (mods/<id>/ for a published package, data/ for the editor's live workspace).
 */
export async function compile(recipe: Recipe, getFile: GetFile, opts: CompileOptions = {}): Promise<CompileResult> {
  assertValid(recipe);
  const language = opts.language ?? 'english';
  const prefix = opts.prefix ?? '';
  const warnings: string[] = [];
  const touchedPids = new Set<number>();

  const lstBytes = await requireFile(getFile, 'proto/items/items.lst', 'map pids to proto files');
  const dir: ProtoDir = { lst: parseLst(lstBytes), loaded: new Map() };
  const art = new ArtChecker(getFile);

  const outProtos = new Map<string, Uint8Array>();
  const msgEdits = new Map<string, MsgEdit[]>();
  const pushMsg = (file: string, edit: MsgEdit) => {
    msgEdits.set(file, [...(msgEdits.get(file) ?? []), edit]);
  };

  // --- existing items -----------------------------------------------------
  for (const [key, value] of Object.entries(recipe.targets ?? {})) {
    const itemMatch = ITEM_KEY_RE.exec(key);
    if (itemMatch) {
      const pid = Number(itemMatch[1]);
      const target = value as ItemTarget;
      const base = await loadProto(dir, getFile, pid);
      touchedPids.add(pid);

      if (target.fields && Object.keys(target.fields).length) {
        await art.check(`item ${pid}`, target.fields);
        const edited = writeItemProto(base, target.fields);
        const file = lookupProtoFile(dir.lst, protoNumberFromPid(pid))!.toLowerCase();
        outProtos.set(`proto/items/${file}`, edited);
      }
      if (target.name !== undefined || target.description !== undefined) {
        if (base.messageId === -1) {
          throw new RecipeError(
            `Item ${pid} has messageId -1: it carries no name or description in pro_item.msg, so it cannot be renamed.`
          );
        }
        if (target.name !== undefined) pushMsg(ITEM_MSG_FILE, { num: base.messageId + MESSAGE_OFFSET_NAME, text: target.name });
        if (target.description !== undefined) {
          pushMsg(ITEM_MSG_FILE, { num: base.messageId + MESSAGE_OFFSET_DESCRIPTION, text: target.description });
        }
      }
      continue;
    }

    const msgMatch = MSG_KEY_RE.exec(key);
    if (msgMatch) pushMsg(msgMatch[1].toLowerCase(), { num: Number(msgMatch[2]), text: value as string });
  }

  // --- new items ----------------------------------------------------------
  let lst = dir.lst;
  for (const item of recipe.newItems ?? []) {
    const template = await loadProto(dir, getFile, item.template);
    touchedPids.add(item.template);
    touchedPids.add(item.pid);

    await art.check(`new item ${item.pid}`, item.fields);
    const messageId = conventionalMessageId(item.pid);
    const bytes = cloneItemProto(template, { pid: item.pid, messageId, fields: item.fields });
    const filename = `${String(item.pid).padStart(8, '0')}.pro`;
    outProtos.set(`proto/items/${filename}`, bytes);
    lst = setLine(lst, item.pid, filename);

    pushMsg(ITEM_MSG_FILE, { num: messageId + MESSAGE_OFFSET_NAME, text: item.name });
    if (item.description !== undefined) {
      pushMsg(ITEM_MSG_FILE, { num: messageId + MESSAGE_OFFSET_DESCRIPTION, text: item.description });
    }
  }

  warnings.push(...art.warnings);

  const files: OverlayFile[] = [];
  for (const [path, bytes] of outProtos) files.push({ path: prefix + path, bytes });

  if ((recipe.newItems ?? []).length) {
    const padded = lst.lines.filter((l) => l.split(' ')[0].trim() === LST_FILLER).length;
    if (padded > 0) {
      warnings.push(
        `items.lst was padded with ${padded} placeholder lines so new items could claim absolute pids. ` +
          'This is expected: the list is positional, and absolute claims are what let two mods add items without colliding.'
      );
    }
    files.push({ path: `${prefix}proto/items/items.lst`, bytes: serializeLst(lst) });
  }

  // --- message files ------------------------------------------------------
  for (const [file, edits] of msgEdits) {
    const path = `text/${language}/${file}`;
    const bytes = await requireFile(getFile, path, 'apply a text change');
    const msg = parseMsg(bytes);
    for (const e of edits) {
      if (!getEntry(msg, e.num) && !file.includes('pro_item')) {
        warnings.push(`${path} has no entry ${e.num}; the recipe adds it. Check the number if you meant to edit existing text.`);
      }
    }
    files.push({ path: prefix + path, bytes: applyMsgEdits(msg, edits) });
  }

  files.sort((a, b) => a.path.localeCompare(b.path));
  return { files, warnings, touchedPids: [...touchedPids].sort((a, b) => a - b) };
}

// --------------------------------------------------------------------- merge

export type ConflictKind = 'field' | 'name' | 'description' | 'msg' | 'newItemPid';

export interface Conflict {
  kind: ConflictKind;
  /** The target key, e.g. `item:11` or `msg:game/pro_item.msg:1100`. */
  target: string;
  /** Field name for kind 'field'; absent otherwise. */
  field?: string;
  a: { recipe: string; value: number | string };
  b: { recipe: string; value: number | string };
  /** Which recipe's value the merge kept. Always the later one: load order wins. */
  winner: string;
}

export interface MergeResult {
  merged: Recipe;
  conflicts: Conflict[];
}

/**
 * Composes recipes in load order. Disjoint field sets on the same item merge
 * silently — the point of the whole format. Same-field collisions are reported
 * and the later recipe wins, which is exactly the rule mods_order.txt already
 * gives users for whole files, applied one level finer.
 */
export function merge(recipes: Recipe[], opts: { id?: string; name?: string; version?: string } = {}): MergeResult {
  const conflicts: Conflict[] = [];
  const targets: Record<string, ItemTarget | string> = {};
  const newItems: NewItem[] = [];
  const pidOwner = new Map<number, string>();

  for (const recipe of recipes) {
    assertValid(recipe);
    for (const [key, value] of Object.entries(recipe.targets ?? {})) {
      if (typeof value === 'string') {
        const prev = targets[key];
        if (typeof prev === 'string' && prev !== value) {
          conflicts.push({
            kind: 'msg',
            target: key,
            a: { recipe: ownerOf(key, recipes, recipe, targets), value: prev },
            b: { recipe: recipe.id, value },
            winner: recipe.id,
          });
        }
        targets[key] = value;
        continue;
      }

      const prev = (targets[key] as ItemTarget | undefined) ?? {};
      const next: ItemTarget = { ...prev, fields: { ...(prev.fields ?? {}) } };

      for (const [field, v] of Object.entries(value.fields ?? {})) {
        const before = next.fields![field];
        if (before !== undefined && before !== v) {
          conflicts.push({
            kind: 'field',
            target: key,
            field,
            a: { recipe: sourceOfField(recipes, recipe, key, field), value: before },
            b: { recipe: recipe.id, value: v },
            winner: recipe.id,
          });
        }
        next.fields![field] = v;
      }

      for (const k of ['name', 'description'] as const) {
        const v = value[k];
        if (v === undefined) continue;
        if (next[k] !== undefined && next[k] !== v) {
          conflicts.push({
            kind: k,
            target: key,
            a: { recipe: sourceOfText(recipes, recipe, key, k), value: next[k]! },
            b: { recipe: recipe.id, value: v },
            winner: recipe.id,
          });
        }
        next[k] = v;
      }

      if (!Object.keys(next.fields!).length) delete next.fields;
      targets[key] = next;
    }

    for (const item of recipe.newItems ?? []) {
      const owner = pidOwner.get(item.pid);
      if (owner !== undefined && owner !== recipe.id) {
        conflicts.push({
          kind: 'newItemPid',
          target: `item:${item.pid}`,
          a: { recipe: owner, value: item.pid },
          b: { recipe: recipe.id, value: item.pid },
          winner: recipe.id,
        });
        newItems.splice(newItems.findIndex((n) => n.pid === item.pid), 1);
      }
      pidOwner.set(item.pid, recipe.id);
      newItems.push(item);
    }
  }

  const merged: Recipe = {
    schema: RECIPE_SCHEMA,
    // The generated id must itself be a legal id — a merged recipe is a recipe,
    // and the installer will make a mods/<id>/ directory out of it.
    id: opts.id ?? `merged-${recipes.map((r) => r.id).join('-')}`.replace(/[^a-z0-9-]/g, '').slice(0, 64),
    name: opts.name ?? recipes.map((r) => r.name).join(' + '),
    version: opts.version ?? '1.0.0',
    notes: `Merged from ${recipes.length} recipes in load order: ${recipes.map((r) => `${r.id}@${r.version}`).join(', ')}.`,
  };
  if (Object.keys(targets).length) merged.targets = targets;
  if (newItems.length) merged.newItems = newItems;
  return { merged, conflicts };
}

/** Which earlier recipe last set this field — for readable conflict reports. */
function sourceOfField(recipes: Recipe[], upTo: Recipe, key: string, field: string): string {
  let owner = '(unknown)';
  for (const r of recipes) {
    if (r === upTo) break;
    const t = r.targets?.[key];
    if (t && typeof t !== 'string' && t.fields && field in t.fields) owner = r.id;
  }
  return owner;
}

function sourceOfText(recipes: Recipe[], upTo: Recipe, key: string, which: 'name' | 'description'): string {
  let owner = '(unknown)';
  for (const r of recipes) {
    if (r === upTo) break;
    const t = r.targets?.[key];
    if (t && typeof t !== 'string' && t[which] !== undefined) owner = r.id;
  }
  return owner;
}

function ownerOf(key: string, recipes: Recipe[], upTo: Recipe, _targets: Record<string, unknown>): string {
  let owner = '(unknown)';
  for (const r of recipes) {
    if (r === upTo) break;
    if (r.targets && key in r.targets) owner = r.id;
  }
  return owner;
}

// ---------------------------------------------------------------------- diff

export interface DiffOptions {
  language?: string;
  meta?: Partial<Pick<Recipe, 'id' | 'name' | 'version' | 'author' | 'engineMin' | 'notes'>>;
}

export interface DiffResult {
  recipe: Recipe;
  /**
   * Changes the recipe format cannot express — new or edited files that are not
   * item protos or .msg entries (art, scripts, maps). These belong to the HOSTED
   * package class; the caller decides whether to ship them alongside.
   */
  unrepresented: { path: string; reason: string }[];
}

/**
 * The inverse of `compile`: given the pristine files and an edited workspace,
 * recover the recipe. This is what the editor UI and the MCP server emit — the
 * user edits a form or an agent writes bytes, and the shareable artefact is
 * derived, never hand-written.
 */
export function diff(
  originalFiles: Map<string, Uint8Array>,
  editedFiles: Map<string, Uint8Array>,
  opts: DiffOptions = {}
): DiffResult {
  const language = opts.language ?? 'english';
  const targets: Record<string, ItemTarget | string> = {};
  const unrepresented: { path: string; reason: string }[] = [];

  const norm = (p: string) => p.replace(/\\/g, '/').toLowerCase();
  const originals = new Map([...originalFiles].map(([k, v]) => [norm(k), v]));

  // messageId -> pid, so a changed msg entry can be folded back into the item
  // target it names rather than surfacing as a bare message number.
  const nameOwners = new Map<number, number>();
  const descOwners = new Map<number, number>();
  for (const [path, bytes] of originals) {
    if (!/^proto\/items\/.+\.pro$/.test(path)) continue;
    try {
      const p = parseItemProto(bytes);
      if (p.messageId !== -1) {
        nameOwners.set(p.messageId + MESSAGE_OFFSET_NAME, p.pid);
        descOwners.set(p.messageId + MESSAGE_OFFSET_DESCRIPTION, p.pid);
      }
    } catch {
      // Not an item proto — critters and scenery share the directory shape.
    }
  }

  const itemTarget = (pid: number): ItemTarget => {
    const key = `item:${pid}`;
    const existing = targets[key];
    if (existing && typeof existing !== 'string') return existing;
    const fresh: ItemTarget = {};
    targets[key] = fresh;
    return fresh;
  };

  for (const [rawPath, editedBytes] of editedFiles) {
    const path = norm(rawPath);
    const originalBytes = originals.get(path);

    if (/^proto\/items\/.+\.pro$/.test(path)) {
      if (!originalBytes) {
        unrepresented.push({
          path,
          reason:
            'A proto that has no original cannot be diffed into a field list — a new item must record the template it was cloned from. ' +
            'Create it through newItems (the editor records the template) rather than by dropping a file in.',
        });
        continue;
      }
      const before = parseItemProto(originalBytes);
      const after = parseItemProto(editedBytes);
      const fields = diffItemProto(before, after);
      if (Object.keys(fields).length) itemTarget(before.pid).fields = fields;
      continue;
    }

    if (path.endsWith('.msg')) {
      const rel = path.startsWith(`text/${language}/`) ? path.slice(`text/${language}/`.length) : null;
      if (!rel) {
        unrepresented.push({ path, reason: `A .msg outside text/${language}/ has no target syntax in v1.` });
        continue;
      }
      const before: MsgFile = parseMsg(originalBytes ?? new Uint8Array(0));
      const after = parseMsg(editedBytes);
      for (const edit of diffMsg(before, after)) {
        const namePid = rel === ITEM_MSG_FILE ? nameOwners.get(edit.num) : undefined;
        const descPid = rel === ITEM_MSG_FILE ? descOwners.get(edit.num) : undefined;
        if (namePid !== undefined) itemTarget(namePid).name = edit.text;
        else if (descPid !== undefined) itemTarget(descPid).description = edit.text;
        else targets[`msg:${rel}:${edit.num}`] = edit.text;
      }
      continue;
    }

    if (path.endsWith('items.lst')) {
      const beforeLst = originalBytes ? claimedNumbers(parseLst(originalBytes)) : new Set<number>();
      const afterLst = claimedNumbers(parseLst(editedBytes));
      const added = [...afterLst].filter((n) => !beforeLst.has(n));
      if (added.length) {
        unrepresented.push({
          path,
          reason: `items.lst gained ${added.length} entries (${added.slice(0, 5).join(', ')}). New items carry a template pid that bytes alone do not record — declare them in newItems.`,
        });
      }
      continue;
    }

    if (originalBytes && sameBytes(originalBytes, editedBytes)) continue;
    unrepresented.push({
      path,
      reason: 'Not an item proto or a message entry. Art, scripts and maps are bytes, not fields — ship them as a HOSTED package.',
    });
  }

  for (const [key, value] of Object.entries(targets)) {
    if (typeof value !== 'string' && !value.fields && value.name === undefined && value.description === undefined) {
      delete targets[key];
    }
  }

  const recipe: Recipe = {
    schema: RECIPE_SCHEMA,
    id: opts.meta?.id ?? 'untitled-mod',
    name: opts.meta?.name ?? 'Untitled mod',
    version: opts.meta?.version ?? '1.0.0',
    ...(opts.meta?.author ? { author: opts.meta.author } : {}),
    ...(opts.meta?.engineMin ? { engineMin: opts.meta.engineMin } : {}),
    ...(opts.meta?.notes ? { notes: opts.meta.notes } : {}),
  };
  if (Object.keys(targets).length) recipe.targets = targets;
  return { recipe, unrepresented };
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
